import React, { useState, useEffect, useContext } from "react";
import {
  Box, Grid, Typography, Button, Select, MenuItem, TextField, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, CircularProgress, IconButton, Tooltip, FormControl,
  InputLabel, Autocomplete,
  DialogContentText
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import InfoIcon from "@mui/icons-material/Info";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import darkTheme from "../../style/theme";
import { ThemeProvider } from "@mui/material/styles";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Icon as OpusIcon, Toggle as OpusToggle, RoleBadge, SearchInput, Badge, BADGE_TONES, Button as OpusButton, LoadingState } from "../../components/common/opus";
import AccessLockModal from "../../components/security/AccessLockModal";
import ViewDetailsModal from "../../components/ViewDetailModal.jsx";
import { AuthContext } from "../../contexts/AuthContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import CircularIndeterminate from "../../components/Loader.component";
import UserStatsCard from "../../components/Stats.component.jsx";
import { useAlert } from "../../contexts/AlertContext";
import { useConfirm } from "../../components/common/ConfirmDialog";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ActivityTrackingModal from "../../components/common/ActivityTrackingModal.jsx";
import TimelineIcon from '@mui/icons-material/Timeline';
import { useSession } from "../../contexts/SessionContext";
import { AddCircle } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from '@mui/icons-material/Email';
import apiService from "../../services/api.service";
import StatusChip from "../../components/common/StatusChip";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

// W3C HTML5 email validation pattern — supports standard addresses, punycode TLDs (.xn--...),
// and internal single-label hosts without a dot (e.g. user@localhost).
const isValidEmail = (email) => {
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
    String(email || "").trim()
  );
};


const Users = () => {
  const { userInfo } = useContext(AuthContext);
  const alert = useAlert();
  const confirm = useConfirm();
  const userOrg = userInfo?.data?.organization_id?._id;
  const userOrgName = userInfo?.data?.organization_id?.name;
  const { can, ready } = usePermissions();
  // Permission-based gates (replace role-name checks so a direct/user-level
  // grant — not just an admin/super_admin role — can unlock the matching UI,
  // mirroring the RendererManagement.jsx RBAC fix.
  const canReadOrgs = can("organisations.read");
  const canReadUsers = can("users.read");
  const canCreateUsers = can("users.create");
  const canUpdateUsers = can("users.update");
  const canUpdateUserOrg = can("users.org.update"); // cross-org reassignment — super_admin only today
  const canUpdateUserEmail = can("users.email.update"); // super_admin only today (mirrors canUpdateUserOrg)
  const canManageEntitlements = can("users.entitlements.update");
  const canManageAccessLock = can("security.regionlock.manage");
  // OPUS-1378: literal super_admin check — NOT modeled as a permission-catalog
  // entry like the gates above. The "no email / set password directly" path is a
  // hard privilege boundary that mirrors the backend's actor.isSuperAdmin guard
  // (a role check, not a permission list), so it's derived the same way here.
  const isSuperAdmin = userInfo?.data?.role?.name === "super_admin";
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const { selectedOrgId: sessionOrgId, updateSelectedOrgId } = useSession();
  const [selectedOrgId, setSelectedOrgId] = useState(sessionOrgId || "");
  // OPUS-913: follow the global sidebar org selector (per-page selector moved there).
  useEffect(() => {
    if (sessionOrgId && sessionOrgId !== selectedOrgId) {
      setSelectedOrgId(sessionOrgId);
      fetchUsers(sessionOrgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionOrgId]);
  const [usersSummary, setUsersSummary]= useState([])
  const [search, setSearch] = useState(""); // OPUS-913: client-side search (matches design toolbar)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [selectedUserForActivity, setSelectedUserForActivity] = useState(null);
  const [resendInviteDialog, setResendInviteDialog] = useState({
    open: false,
    user: null,
    loading: false
  });
  

  const [dialogState, setDialogState] = useState({
    open: false,
    isEdit: false,
    isSaving: false
  });

  // OPUS-899: per-user entitlements dialog (super_admin only).
  const [entitlementsDialog, setEntitlementsDialog] = useState({
    open: false,
    user: null,
    loadingFetch: false,
    saving: false,
    previewEnabled: false,
    previewRendererId: null,
    // OPUS-1045: app access is a generic { <appKey>: bool } map driven by the
    // permission catalog's apps.* domain — no longer hardcoded aether/aedrive.
    apps: {},
    directPermissions: [],
    resourceGrants: [], // OPUS-913: [{ type:'renderer'|'scene', resource_id, actions:['read'] }]
  });
  const [permCatalog, setPermCatalog] = useState([]); // OPUS-898: direct-permission picker options
  const [grantOptions, setGrantOptions] = useState({ renderers: [], scenes: [] }); // OPUS-913 resource-grant picker
  const [lockModal, setLockModal] = useState({ open: false, id: null, name: "" }); // OPUS-913 access lock
  const [userFormData, setUserFormData] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [dataStatus, setDataStatus] = useState({
    orgs: false,
    users: false,
    roles: false
  });


  const handleActivityOpen = (user) => {
    setSelectedUserForActivity(user);
    setActivityModalOpen(true);
  };
  
  const handleActivityClose = () => {
    setActivityModalOpen(false);
    setSelectedUserForActivity(null);
  };
  
  useEffect(() => {
    if (dataStatus.orgs && dataStatus.users && dataStatus.roles) {
      setPageLoading(false);
    }
  }, [dataStatus]);


  const handleResendInvitation = (user) => {
    setResendInviteDialog({
      open: true,
      user: user,
      loading: false
    });
  };

  const confirmResendInvitation = async () => {
    try {
      setResendInviteDialog(prev => ({ ...prev, loading: true }));
      const user = resendInviteDialog.user;
      
      await apiService.resendInvitation(user.user_name);
      
      alert.success(`Invitation email has been resent to ${user.email}`);
      setResendInviteDialog({ open: false, user: null, loading: false });
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert.error(error.message || 'Error resending invitation. Please try again later.');
      setResendInviteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCloseResendDialog = () => {
    setResendInviteDialog({ open: false, user: null, loading: false });
  };

  // OPUS-899: entitlements — fetch fresh on open (grid rows don't carry them).
  const handleOpenEntitlements = async (user) => {
    setEntitlementsDialog({
      open: true, user, loadingFetch: true, saving: false,
      previewEnabled: false, previewRendererId: null, apps: {}, directPermissions: [], resourceGrants: [],
    });
    setGrantOptions({ renderers: [], scenes: [] });
    // OPUS-913: load the user's org renderers + scenes as resource-grant options.
    const orgId = user?.organization_id || user?.organization?._id;
    if (orgId) {
      Promise.all([
        apiService.getRenderersByOrganization(orgId).catch(() => null),
        apiService.getScenesByOrganization(orgId).catch(() => null),
      ]).then(([rResp, sResp]) => {
        const rList = rResp?.data?.renderers || rResp?.data || rResp?.renderers || rResp || [];
        const sList = sResp?.data?.scenes || sResp?.data || sResp?.scenes || sResp || [];
        setGrantOptions({
          renderers: (Array.isArray(rList) ? rList : []).map((r) => ({ id: r.renderer_id || r._id, label: r.renderer_name || r.renderer_id || r._id })).filter((o) => o.id),
          scenes: (Array.isArray(sList) ? sList : []).map((s) => ({ id: s._id || s.id, label: s.scene_name || s.name || s._id })).filter((o) => o.id),
        });
      });
    }
    try {
      if (!permCatalog.length) {
        try {
          const cat = await apiService.getPermissionCatalog();
          setPermCatalog((cat?.data || cat)?.permissions || []);
        } catch (_) { /* picker still works as free text-ish; non-fatal */ }
      }
      const resp = await apiService.getUserData(user.user_name);
      const data = resp?.data || {};
      const ent = data.entitlements || {};
      setEntitlementsDialog((prev) => ({
        ...prev,
        loadingFetch: false,
        // OPUS-913: no preview_renderer object => treat as enabled by default.
        previewEnabled: ent?.preview_renderer?.enabled ?? true,
        previewRendererId: ent?.preview_renderer?.renderer_id || null,
        apps: { ...(ent?.apps || {}) },
        directPermissions: Array.isArray(data.direct_permissions) ? data.direct_permissions : [],
        resourceGrants: Array.isArray(data.resource_grants) ? data.resource_grants : [],
      }));
    } catch (e) {
      setEntitlementsDialog((prev) => ({ ...prev, loadingFetch: false }));
      alert.error(`Failed to load entitlements: ${e.message}`);
    }
  };

  const handleCloseEntitlements = () => {
    setEntitlementsDialog((prev) => ({ ...prev, open: false }));
  };

  const handleSaveEntitlements = async () => {
    const d = entitlementsDialog;
    if (!d.user) return;
    setEntitlementsDialog((prev) => ({ ...prev, saving: true }));
    try {
      const resp = await apiService.updateUserEntitlements(d.user.user_name, {
        preview_renderer: { enabled: d.previewEnabled },
        apps: d.apps || {},
        direct_permissions: d.directPermissions || [],
        resource_grants: d.resourceGrants || [],
      });
      const ent = resp?.data || {};
      alert.success(`Entitlements updated for ${d.user.user_name}`);
      setEntitlementsDialog((prev) => ({
        ...prev, saving: false, open: false,
        previewRendererId: ent?.preview_renderer?.renderer_id || null,
      }));
    } catch (e) {
      setEntitlementsDialog((prev) => ({ ...prev, saving: false }));
      alert.error(`Failed to update entitlements: ${e.message}`);
    }
  };

  // OPUS-985: the backend's user listing is Cognito-backed and truncates a single
  // call at a page size, with no totalCount. Page through it here until a short
  // page confirms the end, so the grid gets the full list and can paginate/search locally.
  const fetchAllUsers = async (orgId) => {
    const PAGE_SIZE = 100;
    const MAX_PAGES = 100; // safety cap in case the backend ignores limit/page
    let all = [];
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const data = await apiService.getUsersByOrganization(orgId, { limit: PAGE_SIZE, page });
      const pageUsers = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : []);
      all = all.concat(pageUsers);
      if (pageUsers.length < PAGE_SIZE) break;
    }
    return all;
  };

  const fetchUsers = async (orgId = null, orgsList = null) => {
    setLoading(true);
    try {
      const userData = await fetchAllUsers(orgId || userOrg);
      const formattedUsers = userData.map((user) => ({
        id: user.Username,
        user_name: user.Username,
        name: user.name || user.Attributes?.find((attr) => attr.Name === "name")?.Value || "",
        email: user.Attributes?.find((attr) => attr.Name === "email")?.Value || "",
        role: user?.role?.name || 'N/A',
        roleId: user?.role?._id,
        status: user.status || 'N/A',
        userStatus: user.status,
        organization_id: user.organization ? user.organization._id : null,
        organizationName: user.organization ? user.organization.name : "N/A",
        cognito_group_id: user.cognito_group_id || "",
        rawData: user,
        _id: user._id,
      }));
      setUsers(formattedUsers);
      setLoading(false);
      setDataStatus(prev => ({ ...prev, users: true }));
      // Prefer the freshly-fetched list when provided: on initial load
      // fetchUsers runs right after setOrganizations(data), before the
      // organizations STATE has committed — reading the state here would miss
      // account.limit and show "No limit" for an org that has a seat limit set.
      const selectedOrg = (orgsList || organizations).find(org => org._id === (orgId || userOrg));

      // OPUS: backend's org user list includes soft-deleted users (status "deleted"),
      // which would otherwise inflate the seat-limit count against the org's limit.
      const nonDeletedUsers = formattedUsers.filter(user => user.status !== "deleted");

      setUsersSummary({
        totalUser: nonDeletedUsers.length,
        limit: formattedUsers.length || 0,
        maxLimit: selectedOrg?.account?.limit?.User || 0,
        totalVerifiedUser: formattedUsers.filter(user => user.status === "active").length,
        totalUnverifiedUser: formattedUsers.filter(user => user.status === "inactive").length,
        orgName: selectedOrg?.name || formattedUsers?.organizationName,
        orgLogo: selectedOrg?.logoUrl || ""
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
      setDataStatus(prev => ({ ...prev, users: true }));
    }
  };

  const handleOrgSelect = (event) => {
    const newOrgId = event.target.value;
    setSelectedOrgId(newOrgId);
    updateSelectedOrgId(newOrgId);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    fetchUsers(newOrgId);
    setDataStatus(prev => ({ ...prev, users: false }));
  };

  useEffect(() => {
    if (!ready) return undefined; // wait for permissions to resolve before deciding which path to take
    if (canReadOrgs) {
      // Multi-org visibility (admin/super_admin, or a direct organisations.read
      // grant): load the org list and follow the sidebar org selector.
      apiService.getOrganizationsFromScene()
        .then((data) => {
          setOrganizations(data);
          if (sessionOrgId && data.some(org => org._id === sessionOrgId)) {
            setSelectedOrgId(sessionOrgId);
            fetchUsers(sessionOrgId, data);
          } else if (data && data.length > 0) {
            setSelectedOrgId(data[0]._id);
            updateSelectedOrgId(data[0]._id);
            fetchUsers(data[0]._id, data);
          }

          setDataStatus(prev => ({ ...prev, orgs: true }));
        })
        .catch((error) => {
          console.error("Error fetching organizations:", error);
          setDataStatus(prev => ({ ...prev, orgs: true }));
        });
    } else if (canReadUsers) {
      // No cross-org visibility, but still holds users.read (e.g. a direct
      // grant): fetch just their own org's users, no org switcher.
      setDataStatus(prev => ({ ...prev, orgs: true }));
      fetchUsers(userOrg);
    } else {
      // No users.read/organisations.read at all — nothing to fetch. The Users
      // nav item + ProtectedRoute (both gated on dashboard.users.view) are the
      // real boundary; this just resolves the loading state so an unexpected
      // visit here (bypassed nav/route guard) shows an empty list, not a
      // permanent spinner.
      setDataStatus(prev => ({ ...prev, orgs: true, users: true }));
    }
  }, [ready, canReadOrgs, canReadUsers, sessionOrgId]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await apiService.getRoles();
        setRoles(data);
        setDataStatus(prev => ({ ...prev, roles: true }));
      } catch (error) {
        console.error("Error fetching roles:", error);
        setDataStatus(prev => ({ ...prev, roles: true }));
      }
    };
    fetchRoles();
  }, []);

  const handleUserFormChange = (event) => {
    const { name, value } = event.target;

    if (name === "organization_id") {
      const selectedOrg = organizations.find((org) => org._id === value);
      setUserFormData((prev) => ({
        ...prev,
        [name]: value,
        cognito_group_id: selectedOrg?.cognito_group_id || "",
      }));
    } else {
      setUserFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

const handleAddUser = () => {

  const initialUser = {
    id: `user-${Date.now()}`,
    user_name: "",
    email: "",
    role: "",
    status: "active",
    organization_id: canReadOrgs ? "" : userOrg,
    cognito_group_id: canReadOrgs ? "" : userInfo?.data?.organization_id?.cognito_group_id || "",
    // OPUS-1378: super-admin-only "no email — set password now" path.
    noEmail: false,
    password: "",
    confirmPassword: "",
  };

  setUserFormData(initialUser);
  setDialogState({
    open: true,
    isEdit: false,
    isSaving: false
  });
};

  const handleEditUser = (user) => {
    const editableUser = {
      id: user.id,
      user_name: user.user_name,
      name: user.name,
      email: user.email,
      role: user.roleId ? roles.find(r => r._id === user.roleId) : "",
      status: user.status,
      organization_id: user.organization_id,
      cognito_group_id: user.cognito_group_id,
      rawData: user.rawData,
      _id: user._id
    };

    setUserFormData(editableUser);
    setDialogState({
      open: true,
      isEdit: true,
      isSaving: false,
      roleOnlyEdit: !canUpdateUserOrg
    });
  };


  const handleMarkUserAsDeleted = async (user) => {
    if (!(await confirm({ title: "Mark user as deleted?", message: `Mark ${user.user_name} as deleted? This action can't be undone.`, destructive: true }))) {
      return;
    }
    setLoading(true);
    try {
      await apiService.markUserAsDeleted(user.user_name);
      
      setUsers(prevUsers => prevUsers.filter(u => u.id !== user.id));
          setUsersSummary(prev => ({
          ...prev,
          totalUser: prev.totalUser - 1,
          totalVerifiedUser: user.status === 'active' ? prev.totalVerifiedUser - 1 : prev.totalVerifiedUser,
          totalUnverifiedUser: user.status === 'inactive' ? prev.totalUnverifiedUser - 1 : prev.totalUnverifiedUser,
        }));
        
        alert.success(`User ${user.user_name} has been marked as deleted.`);
      } catch (error) {
      console.error("Error deleting user:", error);
      alert.error(error.message || "Error deleting user. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    const isCurrentlyActive = user.status === "active";
    const newStatus = isCurrentlyActive ? "inactive" : "active";
    const actionText = isCurrentlyActive ? "deactivate" : "activate";
    
    setLoading(true);
    try {
      if (isCurrentlyActive) {
        await apiService.deactivateUser(user.user_name);
      } else {
        await apiService.activateUser(user.user_name);
      }
      
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, status: newStatus } : u
        )
      );
      alert.success(`User ${user.user_name} has been ${actionText}d successfully.`);
      
      setUsersSummary(prev => ({
        ...prev,
        totalVerifiedUser: isCurrentlyActive 
          ? prev.totalVerifiedUser - 1 
          : prev.totalVerifiedUser + 1,
        totalUnverifiedUser: isCurrentlyActive 
          ? prev.totalUnverifiedUser + 1 
          : prev.totalUnverifiedUser - 1,
      }));
    } catch (error) {
      console.error(`Error ${actionText}ing user:`, error);
      alert.error(error.message || `Error ${actionText}ing user. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };


  const handleCloseDialog = () => {
    setDialogState(prev => ({
      ...prev,
      open: false
    }));
    setUserFormData(null);
  };

  const handleSaveUser = async () => {
    if (dialogState.isEdit) {
      if (!userFormData?.role) {
        return;
      }

      if (canUpdateUserEmail && userFormData.email && !isValidEmail(userFormData.email)) {
        alert.warning("Please enter a valid email address.");
        return;
      }

      setDialogState(prev => ({ ...prev, isSaving: true }));

      try {
        const userPayload = {
          user_name: userFormData.user_name,
          role: userFormData?.role?._id,
          name: userFormData.name,
          ...(canUpdateUserOrg && userFormData.organization_id
            ? { organization_id: userFormData.organization_id }
            : {}),
          ...(canUpdateUserEmail && userFormData.email
            ? { email: userFormData.email.trim() }
            : {}),
        };

        await apiService.updateUser(userFormData.id, userPayload);
        handleCloseDialog();
        fetchUsers(selectedOrgId || userOrg);
      } catch (error) {
        console.error("Error updating user role:", error);
        alert.error(error?.message || "Failed to update user. Please try again.");
      } finally {
        setDialogState(prev => ({ ...prev, isSaving: false }));
      }

    } else {
      // OPUS: super-admin-only "no email — set password now" path.
      const noEmail = isSuperAdmin && userFormData.noEmail;

      if (!userFormData.user_name || !userFormData.organization_id) {
        alert.warning("Please fill in all required fields (username and organization)");
        return;
      }

      if (noEmail) {
        if (!userFormData.password || !userFormData.confirmPassword) {
          alert.warning("Please set and confirm a password for this user.");
          return;
        }
        if (userFormData.password !== userFormData.confirmPassword) {
          alert.warning("Passwords do not match.");
          return;
        }
        // Client-side floor only — Cognito's pool policy is authoritative and the
        // backend surfaces a specific message if the password is rejected.
        if (userFormData.password.length < 8) {
          alert.warning("Password must be at least 8 characters long.");
          return;
        }
      } else if (!userFormData.email) {
        alert.warning("Please fill in all required fields (username, email, and organization)");
        return;
      } else if (!isValidEmail(userFormData.email)) {
        alert.warning("Please enter a valid email address.");
        return;
      }

      if (usersSummary.maxLimit > 0 && usersSummary.totalUser >= usersSummary.maxLimit) {
        alert.error(`Seat limit reached (${usersSummary.totalUser}/${usersSummary.maxLimit}). Contact your administrator to increase the limit.`);
        return;
      }

      setDialogState(prev => ({ ...prev, isSaving: true }));

      try {
        const userPayload = {
          user_name: userFormData.user_name,
          role: userFormData?.role?._id,
          organization_id: userFormData.organization_id,
          cognito_group_id: userFormData.cognito_group_id,
          name: userFormData.name,
          // No-email path sends a password (email omitted); otherwise the classic
          // email-invite flow.
          ...(noEmail
            ? { password: userFormData.password }
            : { email: userFormData.email.trim() }),
        };

        await apiService.createUser(userPayload);
        handleCloseDialog();
        fetchUsers(selectedOrgId || userOrg);
      } catch (error) {
        console.error("Error creating user:", error);
        // OPUS-1148: surface the backend's specific reason (username/email already
        // exists, seat limit reached, …) rather than a generic message.
        alert.error(error?.message || "Failed to create user. Please try again.");
      } finally {
        setDialogState(prev => ({ ...prev, isSaving: false }));
      }
    }
  };

  const handleDetailsOpen = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedUser(null);
  };

  const columns = [
    { field: "user_name", headerName: "User name", flex: 1, minWidth: 200, renderCell: (p) => <span style={{ display: "block", fontWeight: 650, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.row.user_name}</span> },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180, renderCell: (p) => p.row.email ? <span style={{ display: "block", fontFamily: "var(--font-mono)", color: "var(--text-muted)", fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.row.email}</span> : <span style={{ fontSize: 12.5, color: "var(--text-dim)", fontStyle: "italic" }}>No email</span> },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: (params) => <RoleBadge role={params.row.role} />,
    },
    {
      field: "organizationName",
      headerName: "Organisation",
      flex: 1,
      hide: !canReadOrgs,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) => <StatusChip status={params.row.status} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2, 
      renderCell: (params) => (
        <Box sx={{ display: "flex" }}>
          {/* <IconButton onClick={() => handleDetailsOpen(params.row)} color="primary" title="View Details">
            <OpusIcon name="eye" size={18} />
          </IconButton> */}
           <Tooltip title="View activity history">
           <IconButton
                onClick={() => handleActivityOpen(params.row)}
                color="info"
              >
                <OpusIcon name="activity" size={18} />
              </IconButton>
              </Tooltip>
          {editMode && (
            <>
            <Box sx={{ml:1, display: "flex", alignItems: "center"}}>
              <IconButton onClick={() => handleEditUser(params.row)} color="info" title="Edit User">
                <OpusIcon name="edit" size={18} />
              </IconButton>
              {canManageEntitlements && (
                <Tooltip title="Entitlements (preview renderer, app access)">
                  <IconButton onClick={() => handleOpenEntitlements(params.row)} color="seafoamGreen">
                    <VpnKeyIcon />
                  </IconButton>
                </Tooltip>
              )}
              {canManageAccessLock && (
                <Tooltip title="Access lock (region / IP)">
                  <IconButton onClick={() => setLockModal({ open: true, id: params.row.user_name, name: params.row.user_name })} sx={{ color: "var(--text-dim)", "&:hover": { color: "var(--amber)" } }}>
                    <OpusIcon name="lock" size={18} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Change User Status">
              <OpusToggle
                checked={params.row.status === 'active'}
                onChange={() => handleToggleUserStatus(params.row)}
              />
              </Tooltip>
              
              <Tooltip title="Resend Invitation Email">
                <IconButton 
                  onClick={() => handleResendInvitation(params.row)} 
                  color="info"
                  sx={{ ml: 0.5 }}
                >
                  <EmailIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete User">

              <IconButton 
                onClick={() => handleMarkUserAsDeleted(params.row)} 
                color="error"
                sx={{ ml: 0.5 }}
                title="Mark as deleted"
              >
                <OpusIcon name="trash" size={18} />
              </IconButton>
              </Tooltip>
             
              </Box>
              
            </>
            
          )}
        </Box>
      ),
    }
  ];

  const fields = [
    { key: "username", label: "Username" },
    { key: "email", label: "Email" },
    { key: "role", label: "Role" },
    { key: "organizationName", label: "Organisation" },
    { key: "status", label: "Status" },
  ];

  const dialogTitle = dialogState.isEdit
    ? "Edit User"
    : "Add New User";

  const saveButtonText = dialogState.isSaving
    ? <CircularProgress size={24} />
    : (dialogState.isEdit ? "Update Role" : "Save");

  // OPUS-913: client-side filter + unified-card summary metrics (design parity).
  const filteredUsers = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [u.user_name, u.email, u.role, u.organizationName].some((v) => (v || "").toLowerCase().includes(q));
  });
  const sTotalKnown = usersSummary.totalUser != null;
  const sTotal = usersSummary.totalUser || 0;
  const sActive = usersSummary.totalVerifiedUser || 0;
  const sMax = usersSummary.maxLimit || 0;
  const sRemaining = sMax - sTotal;
  const sOver = sMax > 0 && sTotal > sMax;
  const sPct = sMax > 0 ? Math.min(100, Math.round((sTotal / sMax) * 100)) : 0;
  const sCapTone = sOver ? "coral" : (sMax > 0 && sRemaining <= 3) ? "amber" : "green";
  const summaryCards = [
    { icon: "users", label: "Total Users", value: sTotalKnown ? sTotal : "—", tone: "neutral" },
    { icon: "activity", label: "Active Users", value: sActive, tone: "green" },
    { icon: "clock", label: "Seat Limit", value: sMax > 0 ? (sTotalKnown ? `${sTotal} / ${sMax}` : `— / ${sMax}`) : "No limit", tone: sMax > 0 ? sCapTone : "neutral", chip: sMax > 0 && sTotalKnown ? (sOver ? `${sTotal - sMax} over` : `${sRemaining} left`) : null, showBar: sMax > 0 && sTotalKnown, pct: sPct },
  ];

    return (
      <ThemeProvider theme={darkTheme}>
        <Grid container spacing={1} sx={{ height: "100vh" }}>
        <Grid size={12}>
        <DashboardBreadcrumbs
          rightAction={
            canCreateUsers && (
              <OpusButton
                variant="primary"
                icon="plus"
                onClick={handleAddUser}
                disabled={sMax > 0 && sTotal >= sMax}
                title={sMax > 0 && sTotal >= sMax ? `Seat limit reached (${sTotal}/${sMax})` : undefined}
              >
                Add User
              </OpusButton>
            )
          }
        />
        </Grid>
        <Grid size={12} sx={{ height: "calc(100% - 67px)" }}>
        {pageLoading ? (
          <LoadingState rows={8} label="Loading users…" />
        ) : (
          <Box sx={{ width: "100%", height: "100%", borderRadius: "var(--r-lg)", background: "var(--surface-1)", border: "1px solid var(--border)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {/* OPUS-913: summary header — 3 divider-separated metrics, same surface as toolbar + list */}
            {users.length > 0 && (
              <Box sx={{ flexShrink: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, borderBottom: "1px solid var(--border)" }}>
                {summaryCards.map((c, i) => {
                  const t = BADGE_TONES[c.tone] || BADGE_TONES.neutral;
                  return (
                    <Box key={c.label} sx={{ p: "18px 22px", borderRight: { md: i < 2 ? "1px solid var(--border)" : "none" }, borderBottom: { xs: i < 2 ? "1px solid var(--border)" : "none", md: "none" }, display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: "var(--r-md)", background: t.bg, color: t.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><OpusIcon name={c.icon} size={17} /></Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{c.label}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
                        <Typography sx={{ fontSize: 30, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.02em", lineHeight: 1, whiteSpace: "nowrap" }}>{c.value}</Typography>
                        {c.chip && <Badge tone={c.tone}>{c.chip}</Badge>}
                      </Box>
                      {c.showBar && (
                        <Box sx={{ height: 5, borderRadius: 999, background: "var(--surface-3)", overflow: "hidden", mt: 0.25 }}>
                          <Box sx={{ width: `${c.pct}%`, height: "100%", borderRadius: 999, background: `var(--${c.tone})` }} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            {/* toolbar — same surface, no divider above */}
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.75, p: "14px 18px", flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <SearchInput value={search} onChange={setSearch} placeholder="Search users…" width={300} />
                <Typography sx={{ fontSize: 13, color: "var(--text-dim)", fontWeight: 600, whiteSpace: "nowrap" }}>{filteredUsers.length} of {users.length}</Typography>
              </Box>
              {canUpdateUsers && (
                <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, color: "var(--text-muted)", fontWeight: 600, cursor: "pointer" }}>
                  Edit mode <OpusToggle checked={editMode} onChange={(v) => setEditMode(v)} />
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, width: "100%", position: "relative", px: 2, pb: 1 }}>
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0,0,0,0.3)",
                    zIndex: 1
                  }}
                >
                  <CircularIndeterminate />
                </Box>
              )}
              <DataGrid
                rows={filteredUsers}
                columns={columns}
                loading={loading}
                rowHeight={52}
                columnHeaderHeight={44}
                disableColumnMenu
                disableRowSelectionOnClick
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                sx={{ height: '100%' }}
              />
            </Box>
    
            <Dialog open={dialogState.open} onClose={handleCloseDialog}>
              <DialogTitle>{dialogTitle}</DialogTitle>
              <DialogContent sx={{ width: 500 }}>
                {dialogState.isEdit ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Username: <strong>{userFormData?.user_name}</strong>
                    </Typography>
                    {canUpdateUserEmail ? (
                      <TextField
                        margin="dense"
                        name="email"
                        label="Email"
                        type="email"
                        fullWidth
                        value={userFormData?.email || ""}
                        onChange={handleUserFormChange}
                        error={Boolean(userFormData?.email && !isValidEmail(userFormData.email))}
                        helperText={
                          userFormData?.email && !isValidEmail(userFormData.email)
                            ? "Please enter a valid email address."
                            : ""
                        }
                        InputLabelProps={{ shrink: true }}
                        placeholder="N/A"
                      />
                    ) : (
                      <Typography variant="subtitle1" gutterBottom>
                        Email: <strong>{userFormData?.email || "N/A"}</strong>
                      </Typography>
                    )}
                    {canUpdateUserOrg ? (
                      <FormControl fullWidth margin="dense" sx={{ mt: 1 }}>
                        <InputLabel>Organisation</InputLabel>
                        <Select
                          name="organization_id"
                          value={userFormData?.organization_id || ""}
                          onChange={handleUserFormChange}
                          label="Organisation"
                        >
                          <MenuItem value="" disabled>
                            Select Organisation
                          </MenuItem>
                          {organizations.map((org) => (
                            <MenuItem key={org._id} value={org._id}>
                              {org.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="subtitle1" gutterBottom>
                        Organisation: <strong>{users.find(u => u.id === userFormData?.id)?.organizationName}</strong>
                      </Typography>
                    )}

                    <TextField
                      margin="dense"
                      name="name"
                      label="Name"
                      type="text"
                      fullWidth
                      value={userFormData?.name || ""}
                      onChange={handleUserFormChange}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mt: 1 }}
                    />

                    <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        name="role"
                        value={userFormData?.role || ""}
                        onChange={handleUserFormChange}
                        label="Role"
                      >
                        <MenuItem value="" disabled>
                          Select Role
                        </MenuItem>
                        {roles.map((role) => (
                          <MenuItem key={role._id} value={role}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  <>
                    <TextField
                      autoFocus
                      margin="dense"
                      name="user_name"
                      label="User Name"
                      type="text"
                      fullWidth
                      helperText="Only lowercase letters, numbers, and underscores are allowed."
                      value={userFormData?.user_name || ""}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                        const modifiedEvent = {
                          ...e,
                          target: {
                            ...e.target,
                            value,
                            name: e.target.name
                          }
                        };
                        handleUserFormChange(modifiedEvent);
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                     <TextField
                      margin="dense"
                      name="name"
                      label="Name"
                      type="text"
                      fullWidth
                      value={userFormData?.name || ""}
                      onChange={handleUserFormChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    {/* OPUS-1378: super-admin-only path to create a user with no email
                        and set a permanent password directly (immediately CONFIRMED). */}
                    {isSuperAdmin && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5, mb: 0.5 }}>
                        <Typography sx={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>
                          No email — set password now
                        </Typography>
                        <OpusToggle
                          checked={!!userFormData?.noEmail}
                          onChange={(checked) => {
                            setUserFormData((prev) => ({
                              ...prev,
                              noEmail: checked,
                              // Clear the fields that don't apply to the chosen mode.
                              ...(checked
                                ? { email: "" }
                                : { password: "", confirmPassword: "" }),
                            }));
                          }}
                        />
                      </Box>
                    )}

                    {isSuperAdmin && userFormData?.noEmail ? (
                      <>
                        <TextField
                          margin="dense"
                          name="password"
                          label="Password"
                          type="password"
                          fullWidth
                          value={userFormData?.password || ""}
                          onChange={handleUserFormChange}
                          autoComplete="new-password"
                          helperText="The user signs in with their username and this password."
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          margin="dense"
                          name="confirmPassword"
                          label="Confirm Password"
                          type="password"
                          fullWidth
                          value={userFormData?.confirmPassword || ""}
                          onChange={handleUserFormChange}
                          autoComplete="new-password"
                          InputLabelProps={{ shrink: true }}
                        />
                      </>
                    ) : (
                      <TextField
                        margin="dense"
                        name="email"
                        label="Email"
                        type="email"
                        fullWidth
                        value={userFormData?.email || ""}
                        onChange={handleUserFormChange}
                        error={Boolean(userFormData?.email && !isValidEmail(userFormData.email))}
                        helperText={
                          userFormData?.email && !isValidEmail(userFormData.email)
                            ? "Please enter a valid email address."
                            : ""
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    )}

                    <FormControl fullWidth margin="dense">
                      <InputLabel>Organisation</InputLabel>
                      <Select
                        name="organization_id"
                        value={userFormData?.organization_id || ""}
                        onChange={handleUserFormChange}
                        label="Organization"
                        disabled={!canReadOrgs} // Disable when the actor can't see other orgs
                      >
                        <MenuItem value="" disabled>
                          Select Organisation
                        </MenuItem>
                        {organizations.map((org) => (
                          <MenuItem key={org._id} value={org._id}>
                            {org.name}
                          </MenuItem>
                        ))}
                      </Select>      
                    </FormControl>
    
                    <FormControl fullWidth margin="dense">
                      <InputLabel>Role</InputLabel>
                      <Select
                        name="role"
                        value={userFormData?.role || ""}
                        onChange={handleUserFormChange}
                        label="Role"
                      >
                        <MenuItem value="" disabled>
                          Select Role
                        </MenuItem>
                        {roles.map((role) => (
                          <MenuItem key={role._id} value={role}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} variant="outlined" color="info">
                  Cancel
                </Button>
                <Button onClick={handleSaveUser} variant="contained" color="seafoamGreen85" disabled={dialogState.isSaving}>
                  {saveButtonText}
                </Button>
              </DialogActions>
            </Dialog>
    
            <ViewDetailsModal
              open={detailsOpen}
              onClose={handleDetailsClose}
              data={selectedUser}
              fields={fields}
              title="User Details"
              theme={darkTheme}
            />

            {selectedUserForActivity && (
              <ActivityTrackingModal
                open={activityModalOpen}
                onClose={handleActivityClose}
                userId={selectedUserForActivity.user_name}
                orgId={selectedUserForActivity.organization_id}
                title={`Activity History for ${selectedUserForActivity.user_name}`}
                showUserSelector={true}
                showOrgActivities={false}  
              />
            )}
          </Box>
        )}
        </Grid>
        </Grid>
        <Dialog
          open={resendInviteDialog.open}
          onClose={() => !resendInviteDialog.loading && handleCloseResendDialog()}
        >
          <DialogTitle>{"Resend Invitation Email"}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "text.secondary" }}>
              Are you sure you want to resend the invitation email to {resendInviteDialog.user?.email}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseResendDialog}
              color="inherit"
              disabled={resendInviteDialog.loading}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmResendInvitation}
              color="seafoamGreen"
              variant="outlined"
              disabled={resendInviteDialog.loading}
              startIcon={resendInviteDialog.loading ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            >
              {resendInviteDialog.loading ? "Sending..." : "Resend Invitation"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* OPUS-899: Entitlements dialog (super_admin) */}
        <Dialog open={entitlementsDialog.open} onClose={handleCloseEntitlements} fullWidth maxWidth="sm">
          <DialogTitle>
            Entitlements{entitlementsDialog.user ? ` — ${entitlementsDialog.user.user_name}` : ""}
          </DialogTitle>
          <DialogContent dividers>
            {entitlementsDialog.loadingFetch ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
                  <Box>
                    <Typography variant="body1">Preview renderer</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entitlementsDialog.previewEnabled
                        ? (entitlementsDialog.previewRendererId
                            ? `Owned renderer: ${entitlementsDialog.previewRendererId}`
                            : "A private preview renderer will be provisioned")
                        : "No private preview renderer"}
                    </Typography>
                  </Box>
                  <OpusToggle
                    checked={entitlementsDialog.previewEnabled}
                    onChange={(v) => setEntitlementsDialog((p) => ({ ...p, previewEnabled: v }))}
                  />
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">App access</Typography>
                {/* OPUS-1045: app toggles are derived from the permission catalog's
                    apps.* domain (union of catalog apps + any already on the user),
                    so new apps appear without code changes. Falls back to the known
                    apps if the catalog hasn't loaded. */}
                {(() => {
                  const APP_LABELS = { aether: "Aether", aedrive: "AeDrive" };
                  const fromCatalog = (permCatalog || [])
                    .map((p) => (/^apps\.([^.]+)\./.exec(typeof p === "string" ? p : "") || [])[1])
                    .filter(Boolean);
                  const fromUser = Object.keys(entitlementsDialog.apps || {});
                  const keys = Array.from(new Set([...fromCatalog, ...fromUser]));
                  const appKeys = keys.length ? keys : ["aether", "aedrive"];
                  return appKeys.map((key) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}>
                      <Typography variant="body1">{APP_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1))}</Typography>
                      <OpusToggle
                        checked={!!(entitlementsDialog.apps || {})[key]}
                        onChange={(v) => setEntitlementsDialog((p) => ({ ...p, apps: { ...(p.apps || {}), [key]: v } }))}
                      />
                    </Box>
                  ));
                })()}
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Direct permissions (advanced)</Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={permCatalog}
                  value={entitlementsDialog.directPermissions}
                  onChange={(e, val) => setEntitlementsDialog((p) => ({ ...p, directPermissions: val }))}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Add permission…" />
                  )}
                  sx={{ mt: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Extra permissions granted directly to this user, on top of their role (additive).
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Resource access (specific renderers / scenes)</Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={grantOptions.renderers}
                  getOptionLabel={(o) => o.label || ""}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={grantOptions.renderers.filter((o) => (entitlementsDialog.resourceGrants || []).some((g) => g.type === "renderer" && g.resource_id === o.id))}
                  onChange={(e, val) => setEntitlementsDialog((p) => ({
                    ...p,
                    resourceGrants: [
                      ...(p.resourceGrants || []).filter((g) => g.type !== "renderer"),
                      ...val.map((o) => ({ type: "renderer", resource_id: o.id, actions: ["read"] })),
                    ],
                  }))}
                  renderInput={(params) => <TextField {...params} placeholder="Grant renderers…" />}
                  sx={{ mt: 0.5 }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  options={grantOptions.scenes}
                  getOptionLabel={(o) => o.label || ""}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={grantOptions.scenes.filter((o) => (entitlementsDialog.resourceGrants || []).some((g) => g.type === "scene" && g.resource_id === o.id))}
                  onChange={(e, val) => setEntitlementsDialog((p) => ({
                    ...p,
                    resourceGrants: [
                      ...(p.resourceGrants || []).filter((g) => g.type !== "scene"),
                      ...val.map((o) => ({ type: "scene", resource_id: o.id, actions: ["read"] })),
                    ],
                  }))}
                  renderInput={(params) => <TextField {...params} placeholder="Grant scenes…" />}
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Grants read access to specific renderers / scenes even when the user can't see the whole org. Enforced in RBAC_MODE=enforce; logged in shadow.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEntitlements} color="info" disabled={entitlementsDialog.saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEntitlements}
              color="seafoamGreen"
              variant="outlined"
              disabled={entitlementsDialog.saving || entitlementsDialog.loadingFetch}
              startIcon={entitlementsDialog.saving ? <CircularProgress size={20} color="inherit" /> : <VpnKeyIcon />}
            >
              {entitlementsDialog.saving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
        <AccessLockModal
          open={lockModal.open}
          onClose={() => setLockModal({ open: false, id: null, name: "" })}
          type="user"
          id={lockModal.id}
          name={lockModal.name}
        />
      </ThemeProvider>
    );
};

export default Users;